import { Dialog, DialogTitle, DialogContent, Stack, Box, Typography, IconButton, TextField, InputAdornment, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Chip } from "@mui/material";
import { ListAlt, Close, Search, Download } from "@mui/icons-material";
import { useState } from "react";
import { Invoice } from "../interfaces/Invoice";
import { formatDate, formatPrice } from "./PayoutDashboardHelper";
import { useNavigate } from "react-router-dom";

type InvoiceModalProps = {
  open: boolean,
  invoices: Invoice[],
  currency: string,
  onClose: () => void
}

export const InvoiceModal = ({ open, onClose, invoices, currency }: InvoiceModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const filteredInvoices = invoices?.filter((inv: Invoice) =>
    inv.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.producer_company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.producer_uid.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.currency.toLowerCase().includes(searchTerm.toLowerCase())
  );

const exportToCSV = () => {
    const headers = [
      "Invoice ID", "Edition Date", "Producer ID", "Producer Name", 
      "Currency", "Operators", "Producer Amount", "Mapper Operator Part", "Error Log"
    ];

    const escapeCSVText = (value: any) => {
      if (value === null || value === undefined) return '""';
      return `"${String(value).replace(/"/g, '""')}"`;
    };

    const escapeCSVNumber = (value: any) => {
      if (value === null || value === undefined || value === "") return '""';
      return String(value).replace('.', ',');
    };

    const csvRows = [
      headers.join(";"), 
      ...filteredInvoices.map((inv: Invoice) => [
        escapeCSVText(inv.id), 
        escapeCSVText(inv.edition_date), 
        escapeCSVText(inv.producer_uid), 
        escapeCSVText(inv.producer_company_name), 
        escapeCSVText(inv.currency), 
        escapeCSVText(inv.operators?.join('-') || ""),
        escapeCSVNumber(inv.total_producer_amount),
        escapeCSVNumber(inv.total_amount_with_vat),  
        escapeCSVText(inv.payout_error_log),
      ].join(";")) 
    ];

    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
    
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `payout_report_${currency}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getNavigateInvoice = (invoiceId: string) => {
    const url = '/invoice?invoice_id=' + invoiceId;
    navigate(url);
  };

  const getNavigateProducer = (producerId: string, currency: string) => {
    const url = '/producer-dashboard?currency=' + currency + '&producerId=' + producerId;
    navigate(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>
      <DialogTitle sx={{ p: 2, px: 3, bgcolor: '#1e293b', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ListAlt /><Typography variant="h6" sx={{ fontWeight: 900 }}>Payable Invoices Details ({currency})</Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>
      <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', gap: 2 }}>
        <TextField fullWidth size="small" placeholder="Filter by currency, company name, producer id or invoice id" onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} sx={{ bgcolor: '#fff' }} />
        <Button variant="outlined" startIcon={<Download />} onClick={exportToCSV} sx={{ fontWeight: 800 }}>Export</Button>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer sx={{ height: '100%' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Invoice ID</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Edition Date</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Currency</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Operators</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Producer ID</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Company Name</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Error Analysis</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Producer Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Mapper Operator Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices?.map((inv: Invoice) => {
                return (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontWeight: 800, verticalAlign: 'middle' }}>
                      <Box
                        component="span"
                        onClick={() => getNavigateInvoice(inv.id)}
                        sx={{ color: '#0b377d', cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle', '&:hover': { textDecoration: 'underline', color: '#2599eb' } }}
                      >
                        {inv.id}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(inv.edition_date)}</TableCell>
                    <TableCell>{inv.currency}</TableCell>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {inv.operators?.map((op) => (
                          <Chip
                            key={op}
                            label={op}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 700, fontSize: '0.65rem' }}
                          />
                        ))}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      <Box
                        component="span"
                        onClick={() => getNavigateProducer(inv.producer_uid, inv.currency)}
                        sx={{ color: '#0b377d', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline', color: '#2599eb' } }}
                      >
                        {inv.producer_uid}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>{inv.producer_company_name}</TableCell>
                    <TableCell>{inv.payout_error_log}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>{formatPrice(inv.total_producer_amount, inv.currency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>{formatPrice(inv.total_amount_with_vat, inv.currency)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};